/**
 * i18n setup — eDMS Web
 *
 * Per drafts/14_i18n_architecture.md:
 * - ICU MessageFormat keys.
 * - en-US (source), ar-SA (Saudi) at GA.
 * - Locale resolution priority:
 *     1. User explicit preference (Users.preferred_locale).
 *     2. IdP-supplied locale claim.
 *     3. Browser Accept-Language.
 *     4. Tenant default.
 *     5. Platform default: en-US.
 * - dir=rtl set automatically on `<html>` for RTL locales.
 */

import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import enUS from './locales/en-US.json';
import arSA from './locales/ar-SA.json';

const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];

export function isRtl(locale: string): boolean {
  const lang = locale.split('-')[0] ?? locale;
  return RTL_LANGUAGES.includes(lang);
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      'en-US': { translation: enUS },
      'ar-SA': { translation: arSA },
    },
    fallbackLng: 'en-US',
    supportedLngs: ['en-US', 'ar-SA'],
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'edms_locale',
    },
    returnNull: false,
  });

// Sync html attributes whenever language changes
function syncHtmlAttributes(locale: string) {
  const html = document.documentElement;
  html.lang = locale;
  html.dir = isRtl(locale) ? 'rtl' : 'ltr';
}

i18n.on('languageChanged', syncHtmlAttributes);
syncHtmlAttributes(i18n.language || 'en-US');

export default i18n;
